// /admin/settings/company — профиль компании установки: название, реквизиты,
// цвета и бланк документов.  Право: user.manage.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/States";
import { companyProfileApi, type CompanyProfile as Profile } from "@/lib/api";

const INPUT = "w-full px-3 py-2 rounded-md bg-surface border border-border text-sm";

type Draft = Omit<Profile, "updated_at">;

export default function CompanyProfile() {
  const q = useQuery({ queryKey: ["admin", "company-profile"], queryFn: companyProfileApi.get });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  return <Editor profile={q.data!} />;
}

function Editor({ profile }: { profile: Profile }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { updated_at: _, ...initial } = profile;
  const [f, setF] = useState<Draft>(initial);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const patch = <K extends keyof Draft>(k: K, v: Draft[K]) => {
    setOk(false);
    setF((prev) => ({ ...prev, [k]: v }));
  };
  const orNull = (s: string | null) => (s && s.trim() ? s.trim() : null);

  const save = useMutation({
    mutationFn: () =>
      companyProfileApi.update({
        ...f,
        contact_email: orNull(f.contact_email),
        contact_phone: orNull(f.contact_phone),
        mail_from: orNull(f.mail_from),
      }),
    onSuccess: (p) => {
      setErr(null);
      setOk(true);
      qc.setQueryData(["admin", "company-profile"], p);
      qc.invalidateQueries({ queryKey: ["system", "settings"] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  });

  async function onFile(file: File | undefined) {
    if (file) patch("letterhead_svg", await file.text());
  }

  const field = (label: string, node: ReactNode, hint?: string) => (
    <label className="block text-sm space-y-1">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      {node}
      {hint && <span className="block text-xs text-text-muted">{hint}</span>}
    </label>
  );
  const color = (k: "brand_color" | "passport_text_color") => (
    <div className="flex gap-2 items-center">
      <input type="color" value={f[k]} onChange={(e) => patch(k, e.target.value.toUpperCase())} />
      <input className={`${INPUT} font-mono`} value={f[k]} onChange={(e) => patch(k, e.target.value)} />
    </div>
  );

  return (
    <>
      <PageHeader eyebrow="admin · settings" title={t("companyProfile.title")} description={t("companyProfile.description")} />
      {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
      <form
        className="card p-6 space-y-4 max-w-3xl"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field(t("companyProfile.name"), <input className={INPUT} value={f.name} onChange={(e) => patch("name", e.target.value)} />)}
          {field(
            t("companyProfile.appTitle"),
            <input className={INPUT} value={f.app_title} onChange={(e) => patch("app_title", e.target.value)} required />,
          )}
          {field(
            t("companyProfile.contactEmail"),
            <input className={INPUT} type="email" value={f.contact_email ?? ""} onChange={(e) => patch("contact_email", e.target.value)} />,
          )}
          {field(
            t("companyProfile.contactPhone"),
            <input className={INPUT} value={f.contact_phone ?? ""} onChange={(e) => patch("contact_phone", e.target.value)} />,
          )}
        </div>
        {field(
          t("companyProfile.requisites"),
          <textarea className={`${INPUT} min-h-[6rem]`} value={f.requisites} onChange={(e) => patch("requisites", e.target.value)} />,
        )}
        {field(
          t("companyProfile.mailFrom"),
          <input className={INPUT} value={f.mail_from ?? ""} onChange={(e) => patch("mail_from", e.target.value)} />,
          t("companyProfile.mailFromHint"),
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field(t("companyProfile.brandColor"), color("brand_color"))}
          {field(t("companyProfile.passportTextColor"), color("passport_text_color"))}
        </div>
        {field(
          t("companyProfile.letterhead"),
          <div className="space-y-2">
            {f.letterhead_svg ? (
              <img
                alt=""
                className="max-h-24 border border-border rounded bg-white p-2"
                src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(f.letterhead_svg)}`}
              />
            ) : (
              <p className="text-xs text-text-muted">{t("companyProfile.letterheadNone")}</p>
            )}
            <div className="flex gap-2">
              <input type="file" accept=".svg,image/svg+xml" onChange={(e) => onFile(e.target.files?.[0])} />
              {f.letterhead_svg && (
                <button type="button" className="btn text-xs" onClick={() => patch("letterhead_svg", null)}>
                  {t("companyProfile.letterheadRemove")}
                </button>
              )}
            </div>
          </div>,
        )}
        <div className="flex items-center gap-3">
          <button className="btn btn-primary" type="submit" disabled={save.isPending}>
            {t("common.save")}
          </button>
          {ok && <span className="text-sm text-text-muted">{t("companyProfile.saved")}</span>}
        </div>
      </form>
    </>
  );
}
